import { LocalStorageKeys } from '../constants/localStorage';
import { clientNames, serviceNames } from '../discovery-service/constants';
import { getClient, getEnvironment, listServices } from '../discovery-service/discoveryService.functions';
import { Service } from '../discovery-service/gen/ai/h2o/cloud/discovery/v1/service_pb';
import { setFeaturestoreApiUrl } from '../featurestore/api';
import { OidcSession } from '../test/mocks/OidcSession';
import { CloudPlatformDiscoveryConfig } from '../utils/contexts';
import { getUriHost, getUrlFromUri } from '../utils/utils';

interface BootstrapResult {
  discovery: CloudPlatformDiscoveryConfig;
  auth: {
    authority: string;
    clientId: string;
  };
}

/**
 * Derives service URLs from the discovery service response
 */
const deriveServiceUrls = (services: Service[]) => {
  const deriveUrl = (serviceName: string) => {
    const service = services.find((s) => s.name === serviceName);
    return getUrlFromUri(service?.uri);
  };

  return {
    aiEngineManagerApiUrl: deriveUrl(serviceNames.aiem),
    // Featurestore URL needs special handling - remove trailing /api because the generated SDK adds it
    featurestoreApiUrl: deriveUrl(serviceNames.featurestore)?.replace(/\/api$/, ''),
    loggingServicesApiUrl: deriveUrl(serviceNames.logging),
    telemetryApiUrl: deriveUrl(serviceNames.telemetry),
    mlopsApiUrl: deriveUrl(serviceNames.mlops),
    orchestratorApiUrl: deriveUrl(serviceNames.orchestrator),
    notebookApiUrl: deriveUrl(serviceNames.notebookAPI),
    secureStore: deriveUrl(serviceNames.secureStore),
    authzUrl: deriveUrl(serviceNames.authz),
    mcAdminCenterApiUrl: deriveUrl(serviceNames.mcAdminCenter),
    auditTrailApiUrl: deriveUrl(serviceNames.auditTrail),
    stsDriveUrl: deriveUrl(serviceNames.stsDrive),
    userDriveUrl: deriveUrl(serviceNames.userDrive),
    workspaceDriveUrl: deriveUrl(serviceNames.workspaceDrive),
  };
};

/**
 * Collects and stores valid API hosts for CORS/security checks
 */
const storeValidHosts = (serviceUrls: Record<string, string | undefined>) => {
  const hosts = Object.values(serviceUrls)
    .filter((url): url is string => Boolean(url))
    .map((url) => getUriHost(url));

  if (hosts.length > 0) {
    window.localStorage.setItem(LocalStorageKeys.VALID_HOSTS, hosts.join(','));
  }
};

/**
 * Sets up localStorage with OIDC key and optionally mock session for development
 */
const setupLocalStorage = (providerUrl: string, platformClientId: string) => {
  const oidcKey = `oidc.user:${providerUrl}:${platformClientId}`;
  window.localStorage.setItem(LocalStorageKeys.OIDC_KEY, oidcKey);

  // Set fake session for using mock server for local development
  if (
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_USE_MOCK_SERVER &&
    !process.env.REACT_APP_USE_IDP
  ) {
    window.localStorage.setItem(oidcKey, JSON.stringify(OidcSession));
  }
};

/**
 * Fetches discovery information and bootstraps the application
 * @throws Error if required configuration is missing
 */
export const bootstrapDiscovery = async (): Promise<BootstrapResult> => {
  const [environmentResponse, clientResponse, listServicesResponse] = await Promise.all([
    getEnvironment({}),
    getClient({ name: clientNames.platform }),
    listServices({ pageSize: 1000 }),
  ]);

  const { environment } = environmentResponse;
  const services = listServicesResponse.services || [];
  const serviceUrls = deriveServiceUrls(services);

  // Set featurestore API URL if available (needed for featurestore SDK)
  if (serviceUrls.featurestoreApiUrl) {
    setFeaturestoreApiUrl(serviceUrls.featurestoreApiUrl);
  }

  const providerUrl = environment?.issuerUrl;
  const platformClientId = clientResponse.client?.oauth2ClientId;
  const h2oCloudEnvironment = environment?.h2oCloudEnvironment;

  // Validate required configuration
  if (!providerUrl || !platformClientId) {
    const errors: string[] = [];
    if (!platformClientId) errors.push('No platform client ID available from the identity provider.');
    if (!providerUrl) errors.push('No platform identity provider location available.');
    throw new Error(errors.join(' '));
  }

  // Setup localStorage
  setupLocalStorage(providerUrl, platformClientId);
  storeValidHosts(serviceUrls);

  return {
    discovery: {
      ...serviceUrls,
      providerUrl,
      platformClientId,
      h2oCloudEnvironment,
    },
    auth: {
      authority: providerUrl,
      clientId: platformClientId,
    },
  };
};
